import { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';

const GENERAL_MAIN_CATEGORY_NAME = 'General Documents';

const FIELD_LIMITS = {
  mainCategoryInput: 60,
  subCategoryInput: 60,
  documentName: 100,
};

const clampValue = (value, maxLength) => {
  if (typeof value !== 'string') return value;
  if (!maxLength) return value;
  return value.slice(0, maxLength);
};

const sanitizeTextValue = (value, fieldKey) => {
  if (typeof value !== 'string') return value;
  const cleaned = value.replace(/[\u0000-\u001F\u007F]/g, '');
  return clampValue(cleaned, FIELD_LIMITS[fieldKey]);
};

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.docs'];
const ARCHIVE_EXTENSIONS = ['.zip', '.rar'];
const MAX_SIZE_MB = 5;
const MAX_ARCHIVE_SIZE_MB = 100;

const MIME_BY_EXTENSION = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.docs': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const getExtension = (name = '') => {
  const cleanName = `${name}`.trim();
  if (!cleanName.includes('.')) return '';
  return `.${cleanName.split('.').pop().toLowerCase()}`;
};

const getBaseName = (name = '') => {
  const cleanName = `${name}`.trim();
  if (!cleanName) return 'Document';
  const lastDot = cleanName.lastIndexOf('.');
  if (lastDot <= 0) return cleanName;
  return cleanName.slice(0, lastDot);
};

const isArchiveFile = (fileName = '') =>
  ARCHIVE_EXTENSIONS.includes(getExtension(fileName));

const isAllowedDocument = (fileName = '', mimeType = '') => {
  const ext = getExtension(fileName);
  return ALLOWED_EXTENSIONS.includes(ext) || ALLOWED_TYPES.includes(mimeType);
};

const RAR_WASM_PUBLIC_PATH = '/unrar.wasm';

export default function DocumentUploadForm({ client, categoryList, setCategoryList, onUploadSuccess }) {
  const rarWasmBinaryRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mainCategoryInput, setMainCategoryInput] = useState('');
  const [selectedMainId, setSelectedMainId] = useState(null);
  const [subCategoryInput, setSubCategoryInput] = useState('');
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [isMainSuggestionOpen, setIsMainSuggestionOpen] = useState(false);
  const [isSubSuggestionOpen, setIsSubSuggestionOpen] = useState(false);
  const mainWrapperRef = useRef(null);
  const subWrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (mainWrapperRef.current && !mainWrapperRef.current.contains(event.target)) {
        setIsMainSuggestionOpen(false);
      }
      if (subWrapperRef.current && !subWrapperRef.current.contains(event.target)) {
        setIsSubSuggestionOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainSuggestions = categoryList.filter((c) =>
    c.name.toLowerCase().includes(mainCategoryInput.toLowerCase()),
  );

  const selectedMain = categoryList.find((c) => c._id === selectedMainId);

  const subSuggestions = (selectedMain?.children || []).filter((s) =>
    s.name.toLowerCase().includes(subCategoryInput.toLowerCase()),
  );

  const isArchiveUploadSelected = !!selectedFile && isArchiveFile(selectedFile.name);

  const handleSessionExpiry = (data) => {
    if (data?.code === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const validateFile = (file) => {
    const ext = getExtension(file.name);
    const isArchive = ARCHIVE_EXTENSIONS.includes(ext);

    if (!isArchive && !isAllowedDocument(file.name, file.type)) {
      alert('Only PDF, DOC, DOCX, DOCS, ZIP, or RAR files are allowed.');
      return false;
    }

    if (isArchive && file.size > MAX_ARCHIVE_SIZE_MB * 1024 * 1024) {
      alert(`Archive size must not exceed ${MAX_ARCHIVE_SIZE_MB}MB.`);
      return false;
    }

    if (!isArchive && file.size > MAX_SIZE_MB * 1024 * 1024) {
      alert(`File size must not exceed ${MAX_SIZE_MB}MB.`);
      return false;
    }

    return true;
  };

  const handleMainInputChange = (e) => {
    setMainCategoryInput(sanitizeTextValue(e.target.value, 'mainCategoryInput'));
    setSelectedMainId(null);
    setIsMainSuggestionOpen(true);
    setSubCategoryInput('');
    setSelectedSubId(null);
  };

  const handleSubInputChange = (e) => {
    setSubCategoryInput(sanitizeTextValue(e.target.value, 'subCategoryInput'));
    setSelectedSubId(null);
    setIsSubSuggestionOpen(true);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!validateFile(file)) {
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (!validateFile(file)) return;
      setSelectedFile(file);
    }
  };

  const loadRarWasmBinary = async () => {
    if (rarWasmBinaryRef.current) return rarWasmBinaryRef.current;
    const wasmResponse = await fetch(RAR_WASM_PUBLIC_PATH);
    if (!wasmResponse.ok) throw new Error('RAR runtime file could not be loaded.');
    const wasmBinary = await wasmResponse.arrayBuffer();
    rarWasmBinaryRef.current = wasmBinary;
    return wasmBinary;
  };

  const detectArchiveType = async (archiveFile) => {
    try {
      const signature = new Uint8Array(await archiveFile.slice(0, 8).arrayBuffer());
      if (signature[0] === 0x50 && signature[1] === 0x4b) return 'zip';
      if (signature[0] === 0x52 && signature[1] === 0x61 && signature[2] === 0x72 && signature[3] === 0x21) return 'rar';
    } catch (e) {
      console.warn('Could not detect archive signature:', e);
    }
    const ext = getExtension(archiveFile?.name);
    if (ext === '.zip') return 'zip';
    if (ext === '.rar') return 'rar';
    return 'unknown';
  };

  const parseArchiveFilePath = (entryPath) => {
    const normalized = `${entryPath || ''}`.replace(/\\/g, '/');
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length === 0) return null;
    const fileName = parts[parts.length - 1];
    const folderParts = parts.slice(0, -1);
    if (!fileName) return null;
    if (folderParts.length === 0) {
      return { mainCategoryName: GENERAL_MAIN_CATEGORY_NAME, subCategoryName: '', fileName };
    }
    return {
      mainCategoryName: folderParts[0] || GENERAL_MAIN_CATEGORY_NAME,
      subCategoryName: folderParts[1] || '',
      fileName,
    };
  };

  const extractZipUploadItems = async (archiveFile) => {
    const zip = await JSZip.loadAsync(archiveFile);
    const items = [];
    let skippedUnsupported = 0;

    for (const entry of Object.values(zip.files)) {
      if (entry.dir) continue;
      const parsed = parseArchiveFilePath(entry.name);
      if (!parsed) continue;
      if (!isAllowedDocument(parsed.fileName, MIME_BY_EXTENSION[getExtension(parsed.fileName)] || '')) {
        skippedUnsupported++;
        continue;
      }
      const blob = await entry.async('blob');
      const ext = getExtension(parsed.fileName);
      const uploadFile = new File([blob], parsed.fileName, {
        type: MIME_BY_EXTENSION[ext] || blob.type || 'application/octet-stream',
      });
      items.push({
        file: uploadFile,
        mainCategoryName: parsed.mainCategoryName,
        subCategoryName: parsed.subCategoryName,
        docName: getBaseName(parsed.fileName),
      });
    }
    return { items, skippedUnsupported };
  };

  const extractRarUploadItems = async (archiveFile) => {
    const unrarModule = await import('node-unrar-js/esm/index.esm.js');
    const { createExtractorFromData } = unrarModule;
    const archiveBuffer = await archiveFile.arrayBuffer();
    const wasmBinary = await loadRarWasmBinary();
    const extractor = await createExtractorFromData({ data: archiveBuffer, wasmBinary });
    const extracted = extractor.extract({});
    const items = [];
    let skippedUnsupported = 0;

    for (const fileEntry of extracted.files) {
      const header = fileEntry?.fileHeader;
      if (header?.flags?.directory) continue;
      const filePath = header?.name || '';
      const parsed = parseArchiveFilePath(filePath);
      if (!parsed) continue;
      const ext = getExtension(parsed.fileName);
      if (!isAllowedDocument(parsed.fileName, MIME_BY_EXTENSION[ext] || '')) {
        skippedUnsupported++;
        continue;
      }
      const bytes = fileEntry?.extraction;
      if (!bytes) continue;
      const mimeType = MIME_BY_EXTENSION[ext] || 'application/octet-stream';
      const blob = new Blob([bytes], { type: mimeType });
      const uploadFile = new File([blob], parsed.fileName, { type: mimeType });
      items.push({
        file: uploadFile,
        mainCategoryName: parsed.mainCategoryName,
        subCategoryName: parsed.subCategoryName,
        docName: getBaseName(parsed.fileName),
      });
    }
    return { items, skippedUnsupported };
  };

  const getArchiveUploadItems = async (archiveFile) => {
    const archiveType = await detectArchiveType(archiveFile);
    if (archiveType === 'zip') return extractZipUploadItems(archiveFile);
    if (archiveType === 'rar') return extractRarUploadItems(archiveFile);
    throw new Error('Unsupported or invalid archive file.');
  };

  const fetchCategoriesSnapshot = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
    );
    const data = await response.json();
    if (handleSessionExpiry(data)) return null;
    return data?.body?.categories || [];
  };

  const resolveMainCategoryId = async (categoriesRef, mainCategoryName) => {
    const normalizedName = (mainCategoryName || GENERAL_MAIN_CATEGORY_NAME).trim();
    if (!normalizedName) return null;

    const existingMain = categoriesRef.value.find(
      (c) => c?.name?.toLowerCase() === normalizedName.toLowerCase(),
    );
    if (existingMain?._id) return existingMain._id;

    const createRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: normalizedName }),
      },
    );
    const createData = await createRes.json();
    if (handleSessionExpiry(createData)) return null;

    let createdId = createData?.body?._id || createData?.category?._id || createData?._id;
    if (createdId) {
      categoriesRef.value = [...categoriesRef.value, { _id: createdId, name: normalizedName, children: [] }];
      return createdId;
    }

    const refreshed = await fetchCategoriesSnapshot();
    if (!refreshed) return null;
    categoriesRef.value = refreshed;
    setCategoryList(refreshed);

    const refreshedMain = refreshed.find((c) => c?.name?.toLowerCase() === normalizedName.toLowerCase());
    return refreshedMain?._id || null;
  };

  const resolveSubCategoryId = async (categoriesRef, mainId, subCategoryName) => {
    const normalizedSub = (subCategoryName || '').trim();
    if (!normalizedSub) return null;

    const mainCategory = categoriesRef.value.find((c) => String(c?._id) === String(mainId));
    const existingSub = mainCategory?.children?.find(
      (child) => child?.name?.toLowerCase() === normalizedSub.toLowerCase(),
    );
    if (existingSub?._id) return existingSub._id;

    const createRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: normalizedSub, parent: mainId }),
      },
    );
    const createData = await createRes.json();
    if (handleSessionExpiry(createData)) return null;

    let createdId = createData?.body?._id || createData?.category?._id || createData?._id;
    if (createdId) {
      categoriesRef.value = categoriesRef.value.map((category) => {
        if (String(category?._id) !== String(mainId)) return category;
        return { ...category, children: [...(category?.children || []), { _id: createdId, name: normalizedSub }] };
      });
      return createdId;
    }

    const refreshed = await fetchCategoriesSnapshot();
    if (!refreshed) return null;
    categoriesRef.value = refreshed;
    setCategoryList(refreshed);

    const refreshedMain = refreshed.find((c) => String(c?._id) === String(mainId));
    const refreshedSub = refreshedMain?.children?.find(
      (child) => child?.name?.toLowerCase() === normalizedSub.toLowerCase(),
    );
    return refreshedSub?._id || null;
  };

  const uploadDocumentWithCategory = async (categoriesRef, file, mainCategoryName, subCategoryName, docTitle, mainCategoryId, subCategoryId) => {
    const safeMainCategoryName = sanitizeTextValue(mainCategoryName, 'mainCategoryInput');
    const safeSubCategoryName = sanitizeTextValue(subCategoryName, 'subCategoryInput');
    const safeDocTitle = sanitizeTextValue(docTitle || getBaseName(file?.name), 'documentName');

    const mainId = mainCategoryId || (await resolveMainCategoryId(categoriesRef, safeMainCategoryName));
    if (!mainId) throw new Error('Could not resolve main category ID');

    const subId = subCategoryId || (await resolveSubCategoryId(categoriesRef, mainId, safeSubCategoryName));

    const formData = new FormData();
    formData.append('documents', file);
    formData.append('docName', safeDocTitle);
    formData.append('documentCategory', String(mainId));
    if (subId) formData.append('documentSubcategory', String(subId));

    const updateRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${client._id}`,
      {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      },
    );

    if (!updateRes.ok) {
      const errorText = await updateRes.text();
      throw new Error(`Update failed: ${updateRes.status} ${errorText}`);
    }

    const updateData = await updateRes.json();
    if (handleSessionExpiry(updateData)) return null;

    return updateData?.body?.application || updateData?.body || updateData?.application || updateData?.data || null;
  };

  const resetUploadForm = () => {
    setMainCategoryInput('');
    setSelectedMainId(null);
    setSubCategoryInput('');
    setSelectedSubId(null);
    setDocumentName('');
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCategorySubmit = async () => {
    if (!selectedFile || !client?._id) return;

    setIsSubmitting(true);
    try {
      const categoriesRef = { value: categoryList.length ? [...categoryList] : [] };

      if (categoriesRef.value.length === 0) {
        const fetchedCategories = await fetchCategoriesSnapshot();
        if (!fetchedCategories) return;
        categoriesRef.value = fetchedCategories;
        setCategoryList(fetchedCategories);
      }

      const uploadQueue = [];
      let skippedUnsupported = 0;

      if (isArchiveFile(selectedFile.name)) {
        const archiveResult = await getArchiveUploadItems(selectedFile);
        uploadQueue.push(...archiveResult.items);
        skippedUnsupported = archiveResult.skippedUnsupported;
        if (uploadQueue.length === 0) {
          alert('No supported documents found in archive. Only PDF, DOC, DOCX, and DOCS are uploaded.');
          return;
        }
      } else {
        uploadQueue.push({
          file: selectedFile,
          mainCategoryName: sanitizeTextValue(mainCategoryInput, 'mainCategoryInput').trim() || GENERAL_MAIN_CATEGORY_NAME,
          subCategoryName: sanitizeTextValue(subCategoryInput, 'subCategoryInput').trim(),
          docName: sanitizeTextValue(documentName, 'documentName').trim() || getBaseName(selectedFile.name),
          mainCategoryId: selectedMainId,
          subCategoryId: selectedSubId,
        });
      }

      let successCount = 0;
      let failCount = 0;
      let latestApplication = null;

      for (const item of uploadQueue) {
        try {
          const uploadedApplication = await uploadDocumentWithCategory(
            categoriesRef, item.file, item.mainCategoryName, item.subCategoryName,
            item.docName, item.mainCategoryId, item.subCategoryId,
          );
          if (!uploadedApplication) return;
          latestApplication = uploadedApplication;
          successCount++;
        } catch (uploadError) {
          console.error('Upload failed for file:', item?.file?.name, uploadError);
          failCount++;
        }
      }

      if (latestApplication?.documents) {
        onUploadSuccess(latestApplication);
      }

      if (successCount > 0) resetUploadForm();

      if (isArchiveFile(selectedFile.name)) {
        alert(`Archive upload complete. Uploaded ${successCount} file(s), failed ${failCount} file(s), skipped ${skippedUnsupported} unsupported file(s).`);
      } else if (failCount > 0 && successCount === 0) {
        alert('Document upload failed. Please try again.');
      }
    } catch (error) {
      if (isArchiveFile(selectedFile?.name)) {
        console.error('Archive processing failed:', error);
        alert('Could not process archive. Please ensure ZIP/RAR is valid and try again.');
      } else {
        console.error('Error managing categories:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-start p-4 border border-gray-200 rounded-lg">
      <div className="flex flex-col gap-4">
        <div className="relative" ref={mainWrapperRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <input
            value={mainCategoryInput}
            onChange={handleMainInputChange}
            onFocus={() => { if (!isArchiveUploadSelected) setIsMainSuggestionOpen(true); }}
            disabled={isArchiveUploadSelected}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Select or Create..."
          />
          {isMainSuggestionOpen && !isArchiveUploadSelected && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
              {mainSuggestions.length === 0 && !mainCategoryInput && (
                <div className="p-3 text-gray-500 italic text-sm">Start typing to see suggestions...</div>
              )}
              {mainSuggestions.map((cat, idx) => (
                <div
                  key={cat._id || idx}
                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0 text-sm"
                  onClick={() => {
                    setMainCategoryInput(sanitizeTextValue(cat.name, 'mainCategoryInput'));
                    setSelectedMainId(cat._id);
                    setIsMainSuggestionOpen(false);
                    setSubCategoryInput('');
                    setSelectedSubId(null);
                  }}
                >
                  {cat.name}
                </div>
              ))}
              {mainCategoryInput && !categoryList.some((s) => s.name.toLowerCase() === mainCategoryInput.trim().toLowerCase()) && (
                <div className="p-3 hover:bg-gray-100 cursor-pointer text-[#036494] font-medium text-sm" onClick={() => setIsMainSuggestionOpen(false)}>
                  Add &quot;{mainCategoryInput}&quot; to the list
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative" ref={subWrapperRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>
          <input
            value={subCategoryInput}
            onChange={handleSubInputChange}
            onFocus={() => { if (!isArchiveUploadSelected) setIsSubSuggestionOpen(true); }}
            disabled={isArchiveUploadSelected}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Select or Create..."
          />
          {isSubSuggestionOpen && !isArchiveUploadSelected && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
              {subSuggestions.map((sub, idx) => (
                <div
                  key={sub._id || idx}
                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0 text-sm"
                  onClick={() => {
                    setSubCategoryInput(sanitizeTextValue(sub.name, 'subCategoryInput'));
                    setSelectedSubId(sub._id);
                    setIsSubSuggestionOpen(false);
                  }}
                >
                  {sub.name}
                </div>
              ))}
              {subCategoryInput && !(selectedMain?.children || []).some((s) => s.name.toLowerCase() === subCategoryInput.trim().toLowerCase()) && (
                <div className="p-3 hover:bg-gray-100 cursor-pointer text-[#036494] font-medium text-sm" onClick={() => setIsSubSuggestionOpen(false)}>
                  Add &quot;{subCategoryInput}&quot; to the list
                </div>
              )}
              {!selectedMain && <div className="p-3 text-gray-400 italic text-sm">Select a valid Main Category first</div>}
              {selectedMain && subSuggestions.length === 0 && !subCategoryInput && (
                <div className="p-3 text-gray-500 italic text-sm">Start typing to see suggestions...</div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(sanitizeTextValue(e.target.value, 'documentName'))}
            disabled={isArchiveUploadSelected}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Enter Document Name"
          />
        </div>
      </div>

      <div className="h-full flex flex-col gap-4">
        <div
          className={`border-2 border-dashed rounded-lg h-full min-h-[160px] flex flex-col items-center justify-center p-6 text-center transition-colors cursor-pointer ${
            isDragOver ? 'border-[#005787] bg-blue-50' : 'border-gray-300 hover:border-[#005787]'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.docs,.zip,.rar" onChange={handleFileChange} />
          {selectedFile ? (
            <div className="flex flex-col items-center">
              <span className="text-[#005787] font-medium mb-1">{selectedFile.name}</span>
              <span className="text-gray-500 text-sm">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="text-red-500 text-xs mt-2 hover:underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-gray-600 font-medium text-sm">Click to upload or drag and drop</p>
              <p className="text-gray-400 text-xs mt-1">PDF, DOC, DOCX, ZIP, RAR (docs max 5MB, archives max 100MB)</p>
            </>
          )}
        </div>

        <button
          onClick={handleCategorySubmit}
          disabled={isSubmitting}
          className="bg-[#005787] text-white px-4 py-2 rounded-md hover:bg-[#004a73] transition-colors w-full h-10 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Uploading...
            </>
          ) : (
            'Upload'
          )}
        </button>
      </div>
    </div>
  );
}
